import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Download, ExternalLink, Loader2, AlertCircle, FileText } from 'lucide-react';
import { API } from '../App';

export default function PdfViewer() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  const candidateId = searchParams.get('candidateId');
  const docType = searchParams.get('docType');
  const docName = searchParams.get('name') || docType?.replace(/_/g, ' ') || 'Document';
  
  // Get the auth token for embedding
  const token = localStorage.getItem('token');
  
  // Build URLs with token for authentication
  const pdfViewUrl = `${API}/hiring/candidates/${candidateId}/generated/${docType}/view?token=${encodeURIComponent(token || '')}`;
  const downloadUrl = `${API}/hiring/candidates/${candidateId}/generated/${docType}/download?download=true`;

  useEffect(() => {
    // Small delay to allow iframe to load
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  const handleDownload = () => {
    window.open(downloadUrl, '_blank');
  };

  const handleBack = () => {
    navigate('/hiring');
  };

  const handleOpenInNewTab = () => {
    window.open(pdfViewUrl, '_blank');
  };

  if (!candidateId || !docType) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="text-center max-w-md p-6 bg-white rounded-lg shadow-lg">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-slate-800 mb-2">Invalid Request</h2>
          <p className="text-slate-600 mb-4">Missing document parameters</p>
          <Button onClick={handleBack} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Hiring
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={handleBack} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-lg font-semibold capitalize" data-testid="doc-title">
            {docName}
          </h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleOpenInNewTab} data-testid="open-new-tab-btn">
            <ExternalLink className="w-4 h-4 mr-2" />
            Open in New Tab
          </Button>
          <Button onClick={handleDownload} className="bg-green-600 hover:bg-green-700" data-testid="download-btn">
            <Download className="w-4 h-4 mr-2" />
            Download
          </Button>
        </div>
      </div>

      {/* PDF Viewer - using iframe with token in URL */}
      <div className="flex-1 p-4">
        <div className="h-full bg-white rounded-lg shadow-lg overflow-hidden relative" style={{ minHeight: 'calc(100vh - 120px)' }}>
          {/* Loading overlay */}
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
              <div className="text-center">
                <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
                <p className="text-slate-600">Loading document...</p>
              </div>
            </div>
          )}
          
          {/* Fallback UI for browsers without PDF support */}
          {!loading && !iframeLoaded && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 z-5">
              <FileText className="w-24 h-24 text-blue-500 mb-6" />
              <h2 className="text-xl font-semibold text-slate-800 mb-2">{docName}</h2>
              <p className="text-slate-600 mb-6 text-center max-w-md">
                Click below to view or download the document
              </p>
              <div className="flex gap-3">
                <Button variant="outline" size="lg" onClick={handleOpenInNewTab}>
                  <ExternalLink className="w-5 h-5 mr-2" />
                  View Document
                </Button>
                <Button size="lg" onClick={handleDownload} className="bg-green-600 hover:bg-green-700">
                  <Download className="w-5 h-5 mr-2" />
                  Download PDF
                </Button>
              </div>
            </div>
          )}
          
          {/* Iframe for browsers with PDF support */}
          <iframe
            src={pdfViewUrl}
            title={docName}
            className="w-full h-full border-0"
            style={{ minHeight: 'calc(100vh - 120px)' }}
            data-testid="pdf-iframe"
            onLoad={() => setIframeLoaded(true)}
          />
        </div>
      </div>
    </div>
  );
}
